import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { inferJurisdictionKind } from './jurisdiction-maps'

export enum JurisdictionKind {
  STATE = 'state',
  FEDERAL = 'federal',
  UNDECIDED = 'undecided',
}

export type JurisdictionFieldState = {
  kind: JurisdictionKind
  value: string | null
}

export function getJurisdictionFieldStateFromStoredValue(jurisdiction: string | null | undefined, emptyKind: JurisdictionKind): JurisdictionFieldState {
  if (!jurisdiction) return { kind: emptyKind, value: null }

  const inferred = inferJurisdictionKind(jurisdiction)
  if (inferred) return { kind: inferred as JurisdictionKind, value: jurisdiction }
  return { kind: emptyKind, value: null }
}

function jurisdictionValueMatchesKind(value: string, kind: JurisdictionKind): boolean {
  const inferredKind = inferJurisdictionKind(value)
  if (kind === JurisdictionKind.STATE) return inferredKind === 'state'
  if (kind === JurisdictionKind.FEDERAL) return inferredKind === 'federal'
  return false
}

export function getJurisdictionPickerValue(kind: JurisdictionKind, value: string | null): string | null {
  if (!value || kind === JurisdictionKind.UNDECIDED) return null
  return jurisdictionValueMatchesKind(value, kind) ? value : null
}

export function serializeJurisdictionFieldState(state: JurisdictionFieldState): string | null {
  if (state.kind === JurisdictionKind.UNDECIDED) return null
  if (!state.value || !jurisdictionValueMatchesKind(state.value, state.kind)) return null
  return state.value
}

export function validateJurisdictionFieldState(state: JurisdictionFieldState): string | null {
  if (state.kind === JurisdictionKind.UNDECIDED) return null
  if (!state.value || !jurisdictionValueMatchesKind(state.value, state.kind)) return 'Select a jurisdiction'
  return null
}

export function useJurisdictionFieldState(initialJurisdiction: string | null | undefined, emptyKind: JurisdictionKind = JurisdictionKind.UNDECIDED) {
  const initialState = useMemo(() => getJurisdictionFieldStateFromStoredValue(initialJurisdiction, emptyKind), [initialJurisdiction, emptyKind])
  const [fieldState, setFieldState] = useState<JurisdictionFieldState>(() => initialState)
  const [error, setError] = useState<string | null>(null)
  const touchedRef = useRef(false)

  useEffect(() => {
    if (!touchedRef.current) {
      setFieldState(initialState)
    }
  }, [initialState])

  const reset = useCallback(() => {
    touchedRef.current = false
    setFieldState(initialState)
    setError(null)
  }, [initialState])

  const onChange = useCallback((nextState: JurisdictionFieldState) => {
    touchedRef.current = true
    setFieldState(nextState)
    setError(null)
  }, [])

  const validate = useCallback(() => {
    const validationError = validateJurisdictionFieldState(fieldState)
    setError(validationError)
    return validationError
  }, [fieldState])

  return {
    fieldState,
    error,
    isDirty: fieldState.kind !== initialState.kind || fieldState.value !== initialState.value,
    isValid: !validateJurisdictionFieldState(fieldState),
    value: serializeJurisdictionFieldState(fieldState),
    onChange,
    reset,
    validate,
  }
}
